import { Order } from "../pages/orders/types";

const ORDERS_STORAGE_KEY = "peak_reach_orders";
const STORAGE_DATE_KEY = "peak_reach_orders_date";

/**
 * Get today's date as YYYY-MM-DD string
 */
const getTodayDateString = (): string => {
  const today = new Date();
  return today.toISOString().split("T")[0];
};

/**
 * Check if stored orders are from today
 */
const isStoredDataFromToday = (): boolean => {
  const storedDate = localStorage.getItem(STORAGE_DATE_KEY);
  const today = getTodayDateString();
  return storedDate === today;
};

/**
 * Load orders from localStorage if they exist and are from today
 */
export const loadOrdersFromStorage = (): Order[] => {
  try {
    // Check if stored data is from today
    if (!isStoredDataFromToday()) {
      clearOrdersFromStorage();
      return [];
    }

    const storedOrders = localStorage.getItem(ORDERS_STORAGE_KEY);
    if (!storedOrders) {
      return [];
    }

    const orders = JSON.parse(storedOrders) as Order[];
    return orders;
  } catch (error) {
    // Clear corrupted data
    clearOrdersFromStorage();
    return [];
  }
};

/**
 * Save orders to localStorage with today's date
 */
export const saveOrdersToStorage = (orders: Order[]): void => {
  try {
    const today = getTodayDateString();

    // Check if stored data is from a different day - if so, clear old data first
    if (!isStoredDataFromToday() && localStorage.getItem(ORDERS_STORAGE_KEY)) {
      localStorage.removeItem(ORDERS_STORAGE_KEY);
    }

    localStorage.setItem(ORDERS_STORAGE_KEY, JSON.stringify(orders));
    localStorage.setItem(STORAGE_DATE_KEY, today);
  } catch (error) {
    // Handle quota exceeded error
    if (error instanceof DOMException && error.name === "QuotaExceededError") {
      // Try to clear and save again
      try {
        clearOrdersFromStorage();
        localStorage.setItem(ORDERS_STORAGE_KEY, JSON.stringify(orders));
        localStorage.setItem(STORAGE_DATE_KEY, getTodayDateString());
      } catch (retryError) {
        // Failed to save after clearing - silently fail
      }
    }
  }
};

/**
 * Clear orders from localStorage
 */
export const clearOrdersFromStorage = (): void => {
  try {
    localStorage.removeItem(ORDERS_STORAGE_KEY);
    localStorage.removeItem(STORAGE_DATE_KEY);
  } catch (error) {
    // Error clearing orders - silently fail
  }
};

/**
 * Check if we need to clear orders (i.e., date changed)
 * This should be called on app initialization or when date changes
 */
export const checkAndClearExpiredOrders = (): void => {
  if (!isStoredDataFromToday()) {
    clearOrdersFromStorage();
  }
};
